import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { Grid2 as Grid, Button, Alert } from '@mui/material';
import Alerts from '../../components/Dashboard/Alerts';
import CallScript from '../../components/Dashboard/CallScript';
import FacilityHours from '../../components/Dashboard/FacilityHours';
import FacilityInformation from '../../components/Dashboard/FacilityInformation';
import MapView from '../../components/Dashboard/MapView';
import FacilityNotes from '../../components/Dashboard/FacilityNotes';
import InventoryPricingPromotion from '../../components/Grid/InventoryPricingPromotion';
import ActiveReservation from '../../components/Grid/ActiveReservation';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetFacilityByIdQuery, useGetSettingDataQuery } from '../../api/endpoints/apiConfig';
import { facility } from '../../models/DashboardInformation';
import DashboardHeader from '../../components/Dashboard/DashboardHeader'
import { AccordionSummary, AccordionDetails, Typography } from '@mui/material'
import CustomAccordion from '../../components/Accordian/accordian';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FacilityContext } from '../../context/FacilityContext';
import { CALLS_ID_SESSION_CHANGED, CALLS_ID_SESSION_KEY } from '../../utils/callsIdSessionSync';
import { useLookupActiveAircallCall } from '../../hooks/useLookupActiveAircallCall';

const CALLS_ID_MISSING_MESSAGE =
    'Error - Application is not initialized properly.  There is not any Air Call call ID.  Without a call ID, you will not be able to add a credit card payment.     If you are on a call, then use the "Refresh" button in the header to find the Air Call call ID of your current phone call.   Or use the Air Call dialer to start an outbound call.  (The Air Call call ID will be set by the application once the call is answered.)';

const CALLS_ID_CAPTURED_MESSAGE =
    'The active Air Call call has been detected and the session call ID has been captured';

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const facilityId = params.facilityid;
    const { data: facilityData, refetch } = useGetFacilityByIdQuery(facilityId ? parseInt(facilityId) : 0);
    const { data: settingData } = useGetSettingDataQuery(facilityId ? parseInt(facilityId) : 0);
    const [facilityfetch, setFacilityfetch] = useState<facility | null>(null);
    const settings = useMemo(() => settingData?.settings || {}, [settingData?.settings]);
    const context = useContext(FacilityContext);
    const { lookupActiveCall } = useLookupActiveAircallCall();
    const autoLookupAttempted = useRef(false);
    const [callsIdMissing, setCallsIdMissing] = useState(() => {
        const id = sessionStorage.getItem(CALLS_ID_SESSION_KEY);
        return !(id && id.trim());
    });

    useEffect(() => {
        if (autoLookupAttempted.current) {
            return;
        }
        const id = sessionStorage.getItem(CALLS_ID_SESSION_KEY);
        if (id && id.trim()) {
            return;
        }
        autoLookupAttempted.current = true;
        void lookupActiveCall({
            successMessage: CALLS_ID_CAPTURED_MESSAGE,
            showErrorToasts: false,
        });
    }, [lookupActiveCall]);

    useEffect(() => {
        const syncCallsId = () => {
            const id = sessionStorage.getItem(CALLS_ID_SESSION_KEY);
            setCallsIdMissing(!(id && id.trim()));
        };
        syncCallsId();
        window.addEventListener(CALLS_ID_SESSION_CHANGED, syncCallsId);
        const onStorage = (e: StorageEvent) => {
            if (e.key === CALLS_ID_SESSION_KEY) syncCallsId();
        };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(CALLS_ID_SESSION_CHANGED, syncCallsId);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    // Ensure the context is available
    if (!context) {
        throw new Error('FacilityContext must be used within a FacilityProvider');
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { selectedFacility, setSelectedFacility } = context;

    useEffect(() => {
        // Refetch data if facilityId is available
        if (facilityId) {
            refetch();
        }
    }, [facilityId, refetch]);
    useEffect(() => {
        if (facilityData && Array.isArray(facilityData)) {
            // Store the first item of facilityData in sessionStorage
            const facility = facilityData[0];
            sessionStorage.setItem('operatorId', facility.operator_id.toString());
            sessionStorage.setItem('facilityData', JSON.stringify(facilityData));
            sessionStorage.setItem('facilityName', facility.name);
            sessionStorage.setItem("matchedFacilityId", facility.id.toString());
            sessionStorage.setItem("matchedFacilityName", facility.name);
            const facilityId = Number(facility.id);
          if (!isNaN(facilityId)) {
            setSelectedFacility({ label: `${facility.location_code} - ${facility.name}`, id:facility.id }); // Update selectedFacility
          }
            setFacilityfetch(facilityData[0] as facility);
            // Update settings only if they change
            if (settings) {
                sessionStorage.setItem('setting', JSON.stringify(settings));
            }
        }
    }, [facilityData, settings,setSelectedFacility]);  // Watch both facilityData and settings  
    useEffect(() => {
        // Only set `settings` if it has valid data
        if (facilityData && Array.isArray(facilityData)) {
            sessionStorage.setItem('setting', JSON.stringify(settings));
        }
    }, [settings, facilityData]); // This will trigger whenever `settings` changes
    const waitingScreen = () => {
        navigate('/WaitingPage')
    }
    const timezoneMapping: { [key: string]: string } = {
        "America/Los_Angeles": "Pacific",
        "America/New_York": "Eastern",
        "America/Chicago": "Central",
        "America/Denver": "Mountain",
        // Add more if needed
    };
    const formatTime = (timezone: string) => {
        const currentDate = new Date();
        // Format the time using the Intl.DateTimeFormat to display the time as 12-hour format
        const formatter = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZone: timezone,
        });
        // Get the formatted time
        const formattedTime = formatter.format(currentDate);
        // Get the human-readable timezone name (Pacific, Eastern, etc.)
        const timezoneName = timezoneMapping[timezone] || timezone;
        console.log('TimezoneName', timezoneName)
        sessionStorage.setItem('timezoneName', timezoneName);
        return `${formattedTime} ${timezoneName}`;
    };
   
    
    
    return (
        // Main grid container for the dashboard layout
        <>
            <Grid container spacing={2} >
                {callsIdMissing && (
                    <Grid size={{ xs: 12 }}>
                        <Alert severity="error">{CALLS_ID_MISSING_MESSAGE}</Alert>
                    </Grid>
                )}
                {/* Store Space Component */}
                <Grid size={{ xs: 12 }}>
                    {facilityfetch && (
                        <DashboardHeader
                            facility={{
                                name: facilityfetch.name,      // Pass the facility name
                                localTime: formatTime(facilityfetch.timezone),
                                operatorid: facilityfetch.operator_id    // You can calculate or fetch the local time dynamically
                            }}
                        />
                    )}
                </Grid>
                {/* Alerts Component */}
                <Grid size={{ xs: 12 }}>
                    {facilityfetch ? (
                        <Alerts data={facilityfetch} />
                    ) : (
                        <Alerts data={null} />
                    )}

                </Grid>
                {/* Call Script Component */}
                <Grid size={{ xs: 12 }}>
                    {facilityfetch ? (
                        <CallScript data={facilityfetch} />
                    ) : (
                        <CallScript data={null} />
                    )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                    {facilityfetch ? (
                        <FacilityNotes data={facilityfetch} />
                    ) : (
                        <FacilityNotes data={null} />
                    )}

                </Grid>
                {/* ------------------------------------------------ */}
                {/* Facility Information Component */}
                <Grid size={{ xs: 12 }}>
                    <CustomAccordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography variant="h3" color="text.secondary">Facilities
                            </Typography>
                        </AccordionSummary>
                        {facilityfetch ? (<AccordionDetails>
                            <Grid container spacing={2} columns={12} >
                                {/* Facility Information Component */}
                                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                                    {facilityfetch ? (
                                        <FacilityInformation data={facilityfetch} />
                                    ) : (
                                        <FacilityInformation data={null} />
                                    )}
                                </Grid>

                                {/* Facility Hours Component */}
                                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                                    {facilityfetch ? (
                                        <FacilityHours data={facilityfetch} />
                                    ) : (
                                        <FacilityHours data={null} />
                                    )}

                                </Grid>
                                {/* Map View Component */}
                                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                                    {facilityfetch ? (
                                        <MapView data={facilityfetch} />
                                    ) : (
                                        <MapView data={null} />
                                    )}

                                </Grid>
                            </Grid>
                        </AccordionDetails>) : (
                            <AccordionDetails>
                                <Typography>No Facilities Found
                                </Typography>
                            </AccordionDetails>
                        )
                        }
                    </CustomAccordion>
                </Grid>
                {/* ------------------------------ */}
                {/* Inventory Pricing and Promotion Component */}
                <Grid size={{ xs: 12 }}>
                    {facilityfetch ? (

                        <InventoryPricingPromotion data={facilityfetch} />
                    ) : (
                        <InventoryPricingPromotion data={null} />
                    )}
                </Grid>
                {/* Active Reservation Component */}
                <Grid size={{ xs: 12 }}>
                    {facilityfetch ? (
                        <ActiveReservation data={facilityfetch} />
                    ) : (
                        <ActiveReservation data={null} />
                    )}
                </Grid>
                {/* Facility Notes Component */}
            </Grid>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '40px' }}>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ width: '100px' }}
                    onClick={waitingScreen}
                >
                    Back
                </Button>
            </div>
        </>
    )
};

export default Dashboard